import * as React from 'react';
import {
    Box,
    Stack,
    BoxedRow,
    BoxedRowList,
    useDialog,
    ButtonLink,
    ResponsiveLayout,
    GridLayout,
    Divider,
    Title4,
    Logo,
    FixedFooterLayout,
    IconUserAccountRegular,
    skinVars,
} from '@telefonica/mistica';
import {getAuthParams, getAccounts, login, redirectToLogin, type UserAccount} from '../../api';
import {useDocumentTitle} from '../../hooks';
import {I18N} from '../../utils/i18n';
import {trackEvent, setScreenName} from '../../utils/analytics';
import LoadingScreen from '../loading-screen';
import {useApplicationName, useBrand} from '../app-context-provider';

const AccountChooser = (): JSX.Element => {
    const {confirm} = useDialog();
    const appName = useApplicationName();
    const brand = useBrand();
    const {acrValues, loginHint: loginHintFromUrl, clientId = ''} = getAuthParams();
    const [loggedUsers, setLoggedUsers] = React.useState<UserAccount[]>([]);
    const [isLoading, setLoading] = React.useState(true);

    useDocumentTitle(I18N.translate('Acceso a 1$s', appName));

    React.useEffect(() => {
        setScreenName('login_account_chooser');
        getAccounts(brand, clientId, acrValues, loginHintFromUrl)
            .then((result) => {
                setLoggedUsers(result);
                if (!result || !result.length) {
                    redirectToLogin();
                } else {
                    setLoading(false);
                }
            })
            .catch(() => {
                redirectToLogin();
            });
    }, [brand, clientId, acrValues, loginHintFromUrl]);

    const onAccountSelected = (user: UserAccount, idx: number, length: number): void => {
        setLoading(true);
        trackEvent({
            name: 'user_interaction',
            component_type: 'row',
            component_copy: user.type,
            position: `${idx}/${length}`,
            info_detail: user.partial ? 'sesion_cerrada' : 'no_info',
        });
        login(user.ssoToken)
            .then(() => {
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                confirm({
                    title: I18N.translate('Algo ha ido mal'),
                    message: I18N.translate('Puedes volver a intentarlo'),
                    acceptText: I18N.translate('Reintentar'),
                    cancelText: I18N.translate('Cancelar'),
                    onAccept: () => onAccountSelected(user, idx, length),
                });
            });
    };

    if (isLoading) {
        return <LoadingScreen />;
    }

    return (
        <FixedFooterLayout
            footer={
                <ResponsiveLayout>
                    <GridLayout
                        template="6+6"
                        right={null}
                        left={
                            <Box paddingY={16}>
                                <div style={{textAlign: 'center'}}>
                                    <Stack space={16}>
                                        <ButtonLink
                                            small
                                            to={`/account-chooser/management?acr_values=${acrValues.join(
                                                ','
                                            )}`}
                                            trackEvent
                                            withChevron={false}
                                        >
                                            {I18N.translate('Gestionar usuarios')}
                                        </ButtonLink>

                                        {!loginHintFromUrl && (
                                            <Stack space={16}>
                                                <Divider />
                                                <ButtonLink
                                                    small
                                                    onPress={() => {
                                                        redirectToLogin();
                                                    }}
                                                    trackEvent
                                                >
                                                    {I18N.translate('Usar otra cuenta')}
                                                </ButtonLink>
                                            </Stack>
                                        )}
                                    </Stack>
                                </div>
                            </Box>
                        }
                    />
                </ResponsiveLayout>
            }
        >
            <ResponsiveLayout>
                <GridLayout
                    template="6+6"
                    right={null}
                    left={
                        <Box paddingTop={32} paddingBottom={24}>
                            <Stack space={32}>
                                <Stack space={24}>
                                    <Logo size={{mobile: 40, desktop: 48}} />
                                    <Title4 as="h1">{I18N.translate('Selecciona una cuenta')}</Title4>
                                </Stack>
                                <Stack space={0}>
                                    <BoxedRowList>
                                        {loggedUsers.map((user, idx) => (
                                            <BoxedRow
                                                asset={
                                                    <IconUserAccountRegular color={skinVars.colors.brand} />
                                                }
                                                key={user.ssoToken}
                                                {...(user.name
                                                    ? {
                                                          title: user.name,
                                                          description: user.identifier,
                                                      }
                                                    : {
                                                          title: user.identifier,
                                                      })}
                                                onPress={() =>
                                                    onAccountSelected(user, idx, loggedUsers.length)
                                                }
                                                detail={
                                                    user.partial
                                                        ? I18N.translate('Sesión cerrada')
                                                        : undefined
                                                }
                                            />
                                        ))}
                                    </BoxedRowList>
                                </Stack>
                            </Stack>
                        </Box>
                    }
                />
            </ResponsiveLayout>
        </FixedFooterLayout>
    );
};

export default AccountChooser;
